import { useEffect, useState, TouchEvent } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useFotoOffline } from "@/lib/offline/fotosOffline";

interface Props {
  images: string[];
  index: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * In-app fullscreen image viewer. Replaces opening photos in a new browser tab
 * (which on Supabase storage shows the raw bucket URL). Stays inside the app,
 * supports prev/next + swipe + keyboard navigation.
 */
export function ImageLightbox({ images, index, open, onOpenChange }: Props) {
  const [current, setCurrent] = useState(index);
  const [touchStart, setTouchStart] = useState<number | null>(null);

  useEffect(() => {
    if (open) setCurrent(index);
  }, [open, index]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // Close ONLY the lightbox — stop the Escape from also reaching the
        // parent Drawer/Sheet (which would dismiss the whole session).
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        onOpenChange(false);
      }
      if (e.key === "ArrowRight") setCurrent((c) => Math.min(c + 1, images.length - 1));
      if (e.key === "ArrowLeft") setCurrent((c) => Math.max(c - 1, 0));
    };
    // Capture phase so we intercept Escape before the Drawer's own handler.
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, images.length, onOpenChange]);

  // La copia del teléfono si la hay (modo sin conexión); si no, la de siempre.
  // Antes del `return` de abajo: un hook no puede quedarse a medias entre renders.
  const url = useFotoOffline(open ? images[current] : null);

  if (!open || images.length === 0) return null;

  const handleTouchStart = (e: TouchEvent) => setTouchStart(e.touches[0]?.clientX ?? null);
  const handleTouchEnd = (e: TouchEvent) => {
    if (touchStart == null) return;
    const dx = (e.changedTouches[0]?.clientX ?? touchStart) - touchStart;
    if (Math.abs(dx) > 50) {
      if (dx < 0) setCurrent((c) => Math.min(c + 1, images.length - 1));
      else setCurrent((c) => Math.max(c - 1, 0));
    }
    setTouchStart(null);
  };


  // Close ONLY the lightbox, never bubble up to the parent Drawer/Sheet which
  // listens to global pointerdown to dismiss itself.
  const closeLightbox = (e?: React.SyntheticEvent) => {
    if (e) {
      e.stopPropagation();
      // @ts-ignore — native event method exists in synthetic wrappers
      e.nativeEvent?.stopImmediatePropagation?.();
    }
    onOpenChange(false);
  };

  const stop = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    // @ts-ignore
    e.nativeEvent?.stopImmediatePropagation?.();
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[200] bg-black/95 flex items-center justify-center pointer-events-auto"
      onClick={closeLightbox}
      onPointerDown={stop}
      onMouseDown={stop}
      onTouchStart={(e) => { stop(e); handleTouchStart(e); }}
      onTouchEnd={handleTouchEnd}
      style={{
        // The parent Drawer/Dialog is modal (vaul + Radix), which sets
        // `pointer-events: none` on <body>. Because this lightbox is portaled
        // to <body> as a SIBLING of the drawer, it inherits that and every
        // control (the X, arrows, backdrop-to-close) goes dead to touch even
        // though it renders on top. Re-enable pointer events for the subtree.
        pointerEvents: "auto",
        paddingTop: "max(env(safe-area-inset-top, 0px), 16px)",
        paddingBottom: "max(var(--safe-bottom), 16px)",
        paddingLeft: "env(safe-area-inset-left, 0px)",
        paddingRight: "env(safe-area-inset-right, 0px)",
      }}
    >
      <button
        type="button"
        onPointerDown={(e) => { stop(e); onOpenChange(false); }}
        onClick={closeLightbox}
        aria-label="Close"
        className="absolute right-4 z-[210] w-12 h-12 rounded-full bg-black/60 hover:bg-black/75 ring-1 ring-white/60 text-white flex items-center justify-center shadow-xl active:scale-90 transition-transform"
        style={{ top: "calc(max(env(safe-area-inset-top, 0px), 58px) + 12px)", touchAction: "manipulation" }}
      >
        <X className="w-6 h-6" strokeWidth={2.5} />
      </button>

      {images.length > 1 && current > 0 && (
        <button
          type="button"
          onPointerDown={stop}
          onMouseDown={stop}
          onClick={(e) => { stop(e); setCurrent((c) => Math.max(c - 1, 0)); }}
          aria-label="Previous"
          className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
      )}
      {images.length > 1 && current < images.length - 1 && (
        <button
          type="button"
          onPointerDown={stop}
          onMouseDown={stop}
          onClick={(e) => { stop(e); setCurrent((c) => Math.min(c + 1, images.length - 1)); }}
          aria-label="Next"
          className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      )}

      <img
        src={url}
        alt=""
        onPointerDown={stop}
        onMouseDown={stop}
        onClick={stop}
        className="max-w-full max-h-full object-contain select-none"
      />

      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-white/15 text-white text-xs font-medium">
          {current + 1} / {images.length}
        </div>
      )}
    </div>,
    document.body,
  );
}
